import './SocialTemplateEditForm.css';

import React, { useEffect, useState } from 'react';
import { useLoginContext } from '../../../App';
import { useContentContext } from '../../layout/Layout';
import B2BFormContainer from '../../b2b-common/B2BFormContainer';
import EditText from '../../b2b-common/EditText';
import { useSocialContext } from '../B2BSocial';
import LicenseProductsList, { netProductsAcronyms } from '../../b2b-users/components/licenses/LicenseProductsList';
import { fetchProductsData } from '../../../services/b2b-users-apiService';
import { showNotification } from '../../b2b-common/Notification';
import LabelTranslated, { setFieldAndLabelField } from '../../b2b-languages/components/LabelTranslated';
import LocalesTable from '../components/LocalesTable';
import { createTemplate, deleteRelatedTemplateImage, deleteTemplate, fetchTemplateAllInstances, updateTemplate, uploadTemplateImage } from '../../../services/b2b-social-apiServices';
import { generateSelectField } from '../../b2b-common/helpers';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import ShowHideSection from '../../b2b-common/ShowHideSection';
import Banner from '../../b2b-common/Banner';
import TemplateAllInstancesTable from '../components/TemplateAllInstancesTable';
import BooleanToggleField from '../../b2b-common/BooleanToggleField';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import B2BImageCard from '../../b2b-common/B2BImageCard';

export const getSelectedProducts = (productsData, selectedProductsAcronyms) => {
    const findProducts = (products) => {
        let selectedProducts = [];

        products.forEach(product => {
            if (selectedProductsAcronyms.includes(product.productAcronym)) {
                selectedProducts.push(product);
            }
            if (product.childProducts && product.childProducts.length > 0) {
                selectedProducts = selectedProducts.concat(findProducts(product.childProducts));
            }
        });

        return selectedProducts;
    };

    return findProducts(productsData);
};

const SocialTemplateEditForm = ({ template, onSave, onDelete, onCancel }) => {
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-social');

    const { productsData, setProductsData } = useSocialContext();
    const [fetchingProductsData, setFetchingProductsData] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const blankTemplate = {
        socialPostTemplateId: 0,
        socialPostTemplateAcronym: '',
        socialPostTemplateName: '',
        socialPostTemplateName_label: '',
        socialPostTemplateDescription: '',
        socialPostTemplateDescription_label: '',
        socialPostScope: 'ORGANIZATION',
        postTemplateType: 'STATIC',
        templateRequiredProducts: [],
        templateLocales: [],
        socialPostTemplateEnabled: true,
    };

    const [currentTemplate, setCurrentTemplate] = useState(template ? template : blankTemplate);
    const [productsInCurrentTemplate, setProductsInCurrentTemplate] = useState([]);
    const [showInstances, setShowInstances] = useState(false);
    const [currentInstances, setCurrentInstances] = useState([]);
    const [templateImage, setTemplateImage] = useState(null);
    const [deleteTemplateImage, setDeleteTemplateImage] = useState(false);

    const fetchProducts = async () => {
        try {
            if ((loginData) && (!productsData)) {
                setFetchingProductsData(true);
                const data = await fetchProductsData(loginData.accessToken);
                setProductsData(data);
                return data;
            } else {
                return productsData;
            }
        } catch (error) {
            console.error('Failed to fetch products data:', error.message);
            showNotification(setNotification, t('Failed to fetch products data: ') + error.message, 'error', 5000);
        } finally {
            setFetchingProductsData(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [loginData]);

    const getInstancesOfTemplate = async (socialPostTemplateId) => {
        try {
            const instances = await fetchTemplateAllInstances(loginData.accessToken, socialPostTemplateId);
            setCurrentInstances(instances);
        } catch (error) {
            console.error('Failed to fetch template instances:', error.message);
            showNotification(setNotification, t('Failed to fetch template instances: ') + error.message, 'error', 5000);
        }
    };

    useEffect(() => {
        const getProductsInTemplate = async () => {
            if (productsData && template) {
                const productsInTemplate = getSelectedProducts(productsData, template.templateRequiredProducts);
                // console.log('Products in template:', productsInTemplate);
                setProductsInCurrentTemplate(productsInTemplate);
            } else {
                setProductsInCurrentTemplate([]);
            }
        }
        getProductsInTemplate();
        if (template && template.socialPostTemplateId > 0 && template.totalInstances > 0) {
            getInstancesOfTemplate(template.socialPostTemplateId);
        }
        setCurrentTemplate(template ? template : blankTemplate);
    }, [template, productsData]);

    const handleSaveTemplate = async () => {
        const toSaveUpdateTemplate = {
            ...currentTemplate,
            socialPostTemplateName: currentTemplate.socialPostTemplateName_label,
            socialPostTemplateDescription: currentTemplate.socialPostTemplateDescription_label,
        };
        if (!template) { // new template
            try {
                let newTemplate;
                newTemplate = await createTemplate(loginData.accessToken, toSaveUpdateTemplate);
                if (templateImage) {
                    const uplMessage = await uploadTemplateImage(loginData.accessToken, newTemplate.socialPostTemplateId, templateImage);
                    newTemplate.templateImage = uplMessage.extraDetails;
                }
                showNotification(setNotification, t('Template created successfully'), 'success', 3000);
                onSave && onSave(newTemplate);
            } catch (error) {
                console.error('Failed to create template:', error.message);
                showNotification(setNotification, t('Failed to create template: ') + error.message, 'error', 5000);
            }
        } else { // update template
            try {
                let updatedTemplate;
                updatedTemplate = await updateTemplate(loginData.accessToken, currentTemplate.socialPostTemplateId, toSaveUpdateTemplate);
                if (templateImage) {
                    const uplMessage =await uploadTemplateImage(loginData.accessToken, updatedTemplate.socialPostTemplateId, templateImage);
                    updatedTemplate.templateImage = uplMessage.extraDetails;
                } else if (deleteTemplateImage) {
                    await deleteRelatedTemplateImage(loginData.accessToken, updatedTemplate.socialPostTemplateId);
                }
                showNotification(setNotification, t('Template updated successfully'), 'success', 3000);
                onSave && onSave(updatedTemplate);
            } catch (error) {
                console.error('Failed to update template:', error.message);
                showNotification(setNotification, t('Failed to update template: ') + error.message, 'error', 5000);
            }
        }
    }

    const handleDeleteTemplate = () => {
        try {
            deleteTemplate(loginData.accessToken, currentTemplate.socialPostTemplateId);
            showNotification(setNotification, t('Template deleted successfully'), 'success', 3000);
            onDelete && onDelete(currentTemplate);
        } catch (error) {
            console.error('Failed to delete template:', error.message);
            showNotification(setNotification, t('Failed to delete template: ') + error.message, 'error', 5000);
        }
     }

    const handleCancel = () => {
        onCancel && onCancel();
    }

    const handleTemplateProductsChange = (products) => { 
        // console.log('handleTemplateProductsChange:', products);
        const netProductsAcronms = netProductsAcronyms(products);
        // console.log('Net products:', netProductsAcronms);
        setCurrentTemplate({ ...currentTemplate, templateRequiredProducts: netProductsAcronms });
    }

    const templateTypeOptions = [
        { value: 'STATIC', label: t('Static') },
        { value: 'DYNAMIC', label: t('Dynamic') },
    ];

    const socialPostScopeOptions = [
        { value: 'ORGANIZATION', label: t('Organization') },
        { value: 'PLAN', label: t('Plan') },
        { value: 'AREA', label: t('Area') },
        { value: 'AREA_INDICATOR', label: t('Area Indicator') },
    ];

    return (
        <B2BFormContainer
            title={!template ? t('New template') : t('Edit template')}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSaveTemplate },
                { label: t('Delete'), onClick: () => setShowDeleteConfirmation(true) },
                { label: t('Cancel'), onClick: handleCancel },
            ]}
            showDivider={false}
            className={'social-template-edit-form'}
        >
            <div className='social-template-block1'>
                <B2BImageCard
                    imagePath={currentTemplate.templateImage}
                    className={'social-template-img-card'}
                    showChangeButton={true}
                    requestChangeConfirmation={true}
                    onChange={(file) => setTemplateImage(file)}
                    showDeleteButton={true}
                    requestDeleteConfirmation={true}
                    onDelete={() => setDeleteTemplateImage(true)}
                    synchrousUpdate={false}
                />
                <div className="social-template-main-data">
                    <LabelTranslated field={t('Template Name')} labelValue={currentTemplate.socialPostTemplateName_label}
                        locale={`${t('Template Name')} - ${loginData.locale}`} translation={currentTemplate.socialPostTemplateName}
                        className={'social-template-name'}
                        onChange={(event) => {
                            setFieldAndLabelField(currentTemplate, setCurrentTemplate, 'socialPostTemplateName', event.target.value);
                        }}
                    />
                    <EditText object={currentTemplate} objectField='socialPostTemplateAcronym'
                        setObjectField={setCurrentTemplate} label={t('Template Acronym')} />
                    <BooleanToggleField label={t('Enabled')} field='socialPostTemplateEnabled' object={currentTemplate} setObject={setCurrentTemplate} className={'social-template-enable-toggle'} />
                    <LabelTranslated field={t('Template Description')} labelValue={currentTemplate.socialPostTemplateDescription_label}
                        locale={`${t('Template Description')} - ${loginData.locale}`} translation={currentTemplate.socialPostTemplateDescription}
                        rowsNumber={4}
                        className={'social-template-description'}
                        onChange={(event) => {
                            setFieldAndLabelField(currentTemplate, setCurrentTemplate, 'socialPostTemplateDescription', event.target.value);
                        }}
                    />
                </div>
            </div>
            <div className='social-template-block2'>
                <LicenseProductsList
                    title={t('Products in this template')}
                    className="social-template-products-list"
                    productsData={productsData}
                    includedProducts={productsInCurrentTemplate}
                    isFetching={fetchingProductsData}
                    onProductsChange={handleTemplateProductsChange}
                />
                <div className='social-template-locale-table-and-remainings'>
                    {generateSelectField(templateTypeOptions, 
                        {
                            label: t('Template Type'),
                            value: currentTemplate.postTemplateType,
                            onChange: (e) => setCurrentTemplate({ ...currentTemplate, postTemplateType: e.target.value }),
                            className: 'social-template-type-select',
                        }
                    )}
                    {generateSelectField(socialPostScopeOptions, 
                        {
                            label: t('Scope'),
                            value: currentTemplate.socialPostScope,
                            onChange: (e) => setCurrentTemplate({ ...currentTemplate, socialPostScope: e.target.value }),
                            className: 'social-template-scope-select',
                        }
                    )}
                    <LocalesTable locales={currentTemplate.templateLocales}
                        onUpdatedLocales={(locales) => setCurrentTemplate({ ...currentTemplate, templateLocales: locales })}
                    />
                </div>
            </div>
            <ShowHideSection open={showInstances} setOpen={setShowInstances}
                header={
                    <Banner sectionTittle={t('Instances')} message={`${t('Current usage of template')} [${currentTemplate.totalInstances}]`} headingLevel='h4' underline={false} />
                }
                className={'social-template-instances-showhide'}
            >
                <TemplateAllInstancesTable instances={currentInstances} />
            </ShowHideSection>
            <ConfirmationDialog
                title={t('Removal Confirmation Request')}
                mainText={t('Please confirm you want to delete this template')}
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteTemplate}
            />
        </B2BFormContainer>
    );

}

export default SocialTemplateEditForm;